import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { getIncident, addIncidentNote } from "../api"

const severityColor = {
  info: "text-blue-600",
  minor: "text-amber-600",
  major: "text-orange-600",
  critical: "text-red-600",
}

const IncidentDetail = ({ incidentId, onBack }) => {
  const [incident, setIncident] = useState(null)
  const [loading, setLoading] = useState(true)
  const [noteText, setNoteText] = useState("")
  const [noteAction, setNoteAction] = useState("")
  const [noteDayKey, setNoteDayKey] = useState("")
  const [err, setErr] = useState(null)

  const refresh = async () => {
    const r = await getIncident(incidentId)
    setIncident(r?.incident || null)
    setLoading(false)
  }

  useEffect(() => { refresh() }, [incidentId])

  const onAddNote = async () => {
    if (!noteText) return setErr("Note text required")
    setErr(null)
    const r = await addIncidentNote(incidentId, noteText, noteAction || null, noteDayKey || null)
    if (r?.error) return setErr(r.error)
    setNoteText("")
    setNoteAction("")
    setNoteDayKey("")
    refresh()
  }

  if (loading) return <div className="text-text_secondary">Loading...</div>
  if (!incident) return <div className="text-error">Incident not found</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Typography variant="admin_h3">Incident</Typography>
        <Button variant="secondary" onClick={onBack}>Back</Button>
      </div>

      <div className="p-4 rounded-lg border border-bordered bg-paper space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`text-xs px-2 py-0.5 rounded font-medium ${incident.status === "open" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>
              {incident.status}
            </span>
            <span className={`text-sm font-medium ${severityColor[incident.severity] || ""}`}>
              {incident.severity}
            </span>
          </div>
          <span className="text-xs text-text_secondary">{incident.trigger === "auto" ? "Auto-detected" : "Manual"}</span>
        </div>

        <div className="text-sm text-text_secondary">
          Opened: {new Date(incident.openedAt).toLocaleString()}
          {incident.closedAt && ` · Closed: ${new Date(incident.closedAt).toLocaleString()}`}
        </div>
        {incident.reason && <div className="text-sm">Reason: {incident.reason}</div>}
      </div>

      {/* Notes timeline */}
      <div className="space-y-3">
        <Typography variant="admin_h4">Notes</Typography>
        {(incident.notes || []).length === 0 && (
          <div className="text-text_secondary text-sm">No notes yet.</div>
        )}
        {incident.notes?.map((n, i) => (
          <div key={n._id || i} className="p-3 rounded border border-bordered bg-paper">
            <div className="text-sm">{n.text}</div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-text_secondary">{new Date(n.ts).toLocaleString()}{n.action ? ` · Action: ${n.action}` : ""}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Add note form */}
      <div className="p-3 border border-bordered rounded bg-paper space-y-2">
        <textarea className="w-full p-2 border border-bordered rounded bg-canvas text-sm" rows={3} placeholder="Add note..." value={noteText} onChange={(e) => setNoteText(e.target.value)} />
        <div className="grid grid-cols-2 gap-2">
          <input className="p-2 border border-bordered rounded bg-canvas text-sm" placeholder="Action (optional)" value={noteAction} onChange={(e) => setNoteAction(e.target.value)} />
          <input className="p-2 border border-bordered rounded bg-canvas text-sm" type="date" value={noteDayKey} onChange={(e) => setNoteDayKey(e.target.value)} />
        </div>
        <div className="flex gap-2 items-center">
          <Button onClick={onAddNote}>Add Note</Button>
          {err && <span className="text-error text-sm">Error: {err}</span>}
        </div>
      </div>
    </div>
  )
}

export default IncidentDetail
